import React from 'react';
import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import { MembershipItem, MembershipItemType } from '@spike/membership-model';
import { MembershipItemSelector } from 'components/Memberships/Add/Steps/Items/Table/MembershipItemSelector';
import AddOnService from '@spike/addon-service-model';
import { Product } from '@spike/product-model';
import { useSelector } from 'react-redux';
import Service from 'model/Service';
import { RootState } from 'store';
import clsx from 'clsx';
import { CounterField } from 'components/UI/V2/CounterField/CounterField';
import { Checkbox } from 'components/UI/V2/Forms/Checkbox/Checkbox';
import { PriceField2 } from 'components/UI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { CollapsibleAlert } from 'components/UI/V2/CollapsibleAlert/CollapsibleAlert';

interface MembershipItemsTableItemProps {
    item: MembershipItem;
    className?: string;
    onDelete?: (uuid: string) => void;
    onChange?: (item: MembershipItem) => void;
    currentItems: MembershipItem[];
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            padding: '24px 0px',
            borderBottom: '1px solid #D4D4D4',

            [theme.breakpoints.up('md')]: {
                padding: '16px 0px'
            }
        },
        container: {
            width: '100%',
            alignItems: 'center',
            position: 'relative'
        },
        itemName: {
            fontSize: 16,
            lineHeight: 1,
            fontWeight: 600,

            [theme.breakpoints.up('md')]: {
                fontWeight: 500
            }
        },
        itemType: {
            fontSize: 14,
            lineHeight: 1,
            marginTop: 4,
            color: '#7A7A7A'
        },
        itemNameColumn: {
            order: 1,

            [theme.breakpoints.down('sm')]: {
                gridColumn: 'span 2'
            }
        },
        quantityColumn: {
            gap: 8,
            order: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',

            [theme.breakpoints.down('sm')]: {
                order: 3,
                alignSelf: 'flex-end',
                justifySelf: 'flex-start'
            }
        },
        quantityField: {
            gap: '4px !important'
        },
        unlimitedCheckbox: {
            '& div': {
                gap: 6
            },

            '& p': {
                fontSize: 14
            }
        },
        priceColumn: {
            order: 3,

            [theme.breakpoints.down('sm')]: {
                order: 4
            }
        },
        priceLabel: {
            fontSize: 14,
            lineHeight: 1,
            fontWeight: 500,
            marginBottom: 8,

            [theme.breakpoints.up('md')]: {
                display: 'none'
            }
        },
        priceField: {
            '& .MuiInputBase-root': {
                paddingLeft: 0
            },
            '& .MuiInputBase-input': {
                padding: '10px 10px 10px 0px',

                [theme.breakpoints.up('md')]: {
                    padding: '12.5px 10px 12.5px 0px'
                }
            },
            '& .MuiInputAdornment-positionStart': {
                paddingLeft: 10,
                paddingRight: 0
            },
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#D3D3D3 !important'
            },
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: 1,
                borderColor: '#000000 !important'
            }
        },
        totalColumn: {
            order: 4,
            textAlign: 'right',

            [theme.breakpoints.down('sm')]: {
                order: 5
            }
        },
        totalLabel: {
            fontSize: 14,
            lineHeight: 1,
            fontWeight: 500,
            marginBottom: 18,

            [theme.breakpoints.up('md')]: {
                display: 'none'
            }
        },
        totalAmount: {
            fontSize: 15,
            lineHeight: 1,
            fontWeight: 400
        },
        deleteButtonContainer: (props: MembershipItemsTableItemProps) => ({
            textAlign: 'right',
            order: props.item.itemID ? 3 : 0,
            gridColumn: props.item.itemID ? 'span 1' : 'span 3',

            [theme.breakpoints.up('md')]: {
                display: 'none'
            }
        }),
        deleteButton: {
            'gap': 4,
            'alignItems': 'center',
            'display': 'inline-flex',
            'fontFamily': 'Poppins',
            'fontSize': 14,
            'lineHeight': 1,
            'fontWeight': 500,
            'color': '#EF4F57',
            'backgroundColor': 'transparent',
            'border': 'none',
            'cursor': 'pointer',

            '& span': {
                paddingBottom: 2,
                borderBottom: 'solid 1px #EF4F57'
            }
        },
        desktopDeleteButton: {
            right: -32,
            fontSize: 18,
            display: 'none',
            position: 'absolute',
            appearance: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            border: 'none',

            [theme.breakpoints.up('md')]: {
                display: 'block'
            }
        },
        alert: {
            order: 99,
            marginTop: 24,
            gridColumn: 'span 4'
        },
        serviceVariablePrices: {
            gap: 16,
            display: 'flex',
            margin: '0px -16px',
            flexDirection: 'column',

            [theme.breakpoints.up('md')]: {
                gap: 24,
                margin: 0,
                display: 'inline-flex'
            }
        },
        serviceVariablePrice: {
            'gap': 16,
            'display': 'grid',
            'gridTemplateColumns': '35px 55px 85px 1fr',

            [theme.breakpoints.up('md')]: {
                gap: 32,
                gridTemplateColumns: '35px 60px 1fr 1fr'
            },

            '& p': {
                'fontSize': 13,
                'lineHeight': 1.4,
                'fontWeight': 400,

                [theme.breakpoints.down('sm')]: {
                    '&:last-child': {
                        textAlign: 'right'
                    }
                },
                [theme.breakpoints.up('md')]: {
                    fontSize: 14
                },

                '& b': {
                    fontWeight: 600
                }
            }
        }
    })
);

export const MembershipItemsTableItem: React.FC<MembershipItemsTableItemProps> = props => {
    const classes = useStyles(props);

    const servicesState = useSelector((state: RootState) => state.services);
    const productsReducer = useSelector((state: RootState) => state.products);
    const productsState = {
        ...productsReducer,
        list: productsReducer.list.filter(product => product.businessProduct != null)
    };
    const addOnServicesState = useSelector((state: RootState) => state.addOnServices);

    const [service, setService] = React.useState<Service>();

    const isVariablePrice = React.useMemo(() => {
        if (props.item.itemType === MembershipItemType.SERVICE) {
            return (
                service?.pricing.variablePrice?.variables &&
                Object.keys(service.pricing.variablePrice.variables).length > 0
            );
        }

        return false;
    }, [props.item]);

    const itemTypeLabel = React.useMemo(() => {
        if (props.item.itemType === MembershipItemType.SERVICE) {
            return 'Primary Service';
        }

        if (props.item.itemType === MembershipItemType.PRODUCT) {
            return 'Product';
        }

        if (props.item.itemType === MembershipItemType.ADD_ON) {
            return 'Add-On';
        }
    }, [props.item.itemType]);

    const selectorLabel = React.useMemo(() => {
        if (props.item.itemType === MembershipItemType.SERVICE) {
            return 'Select Primary Service';
        }

        if (props.item.itemType === MembershipItemType.PRODUCT) {
            return 'Select Primary Product';
        }

        return 'Select Primary Add-On';
    }, [props.item.itemType]);

    const totalItemPrice = React.useMemo(() => {
        if (props.item.unlimited) {
            return props.item.amount.toFixed(2);
        }

        return Number(props.item.amount * props.item.quantity).toFixed(2);
    }, [props.item.amount, props.item.quantity, props.item.unlimited]);

    const selectorOptions = React.useMemo(() => {
        const selectedNames = props.currentItems.map(item => item.itemName);

        if (props.item.itemType === MembershipItemType.SERVICE) {
            return servicesState.services.map(service => {
                let firstPrice, lastPrice;

                if (service.pricing.variablePrice?.prices) {
                    for (let i = 0; i < service.pricing.variablePrice!.prices.length; i++) {
                        if (i === 0) {
                            firstPrice = service.pricing.variablePrice!.prices[i];
                        }
                        lastPrice = service.pricing.variablePrice!.prices[i];
                    }
                }

                if (service.pricing.fixedPrice) {
                    for (let i = 0; i < service.pricing.fixedPrice.length; i++) {
                        if (i === 0) {
                            firstPrice = service.pricing.fixedPrice[i];
                        }
                        lastPrice = service.pricing.fixedPrice[i];
                    }
                }

                return {
                    label: service.name,
                    price:
                        firstPrice?.price === lastPrice?.price
                            ? `$${firstPrice?.price}`
                            : `$${firstPrice?.price}-$${lastPrice?.price}`,
                    value: service as Service | Product | AddOnService,
                    disabled: selectedNames.includes(service.name)
                };
            });
        }

        if (props.item.itemType === MembershipItemType.PRODUCT) {
            return productsState.list.map(product => ({
                label: product.businessProduct?.name || product.name,
                price: `$${Number(product.businessProduct?.pricing?.price || 0)}`,
                value: product as Service | Product | AddOnService,
                disabled: selectedNames.includes(product.businessProduct?.name || product.name)
            }));
        }

        if (props.item.itemType === MembershipItemType.ADD_ON) {
            return addOnServicesState.services.map(addOnService => ({
                label: addOnService.name,
                price: `$${Number(addOnService.price)}`,
                value: addOnService as Service | Product | AddOnService,
                disabled: selectedNames.includes(addOnService.name)
            }));
        }

        return [];
    }, [
        props.item.itemType,
        props.currentItems,
        servicesState.services,
        productsState.list,
        addOnServicesState.services
    ]);

    const changeItemSelectorHandler = (value: Service | Product | AddOnService) => {
        let itemPrice = 0;
        let itemID = String(value.id);

        if ('pricing' in value) {
            setService(value);

            if (value.pricing.fixedPrice.length) {
                itemPrice = value.pricing.fixedPrice[0].price || 0;
            }

            if (value.pricing.variablePrice?.prices) {
                itemPrice = value.pricing.variablePrice.prices[0].price || 0;
            }
        }

        if ('price' in value) {
            itemPrice = Number(value.price);
        }

        if ('businessProduct' in value) {
            if (value.businessProduct) {
                itemID = String(value.businessProduct.id);
                itemPrice = Number(value.businessProduct.pricing?.price || 0);
            } else {
                itemPrice = Number(value.price || 0);
            }
        }

        props.onChange?.({
            ...props.item,
            itemName: value.name,
            amount: itemPrice,
            itemID: itemID,
            itemType: props.item.itemType
        });
    };

    const changeItemPriceHandler = (value: number | undefined) => {
        if (value === undefined) {
            props.onChange?.({ ...props.item, amount: 0 });

            return;
        }

        props.onChange?.({ ...props.item, amount: value });
    };

    return (
        <div className={classes.root}>
            <div className={clsx(classes.container, props.className)}>
                <div className={classes.deleteButtonContainer}>
                    <button
                        type="button"
                        className={classes.deleteButton}
                        onClick={() => props.onDelete?.(props.item.uuid || '')}
                    >
                        <FontAwesomeIcon icon={faTrashAlt} />
                        <span>Delete</span>
                    </button>
                </div>
                <button
                    type="button"
                    className={classes.desktopDeleteButton}
                    onClick={() => props.onDelete?.(props.item.uuid || '')}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
                {!props.item.itemID && (
                    <MembershipItemSelector
                        label={selectorLabel}
                        options={selectorOptions}
                        onChange={changeItemSelectorHandler}
                    />
                )}
                {props.item.itemID && (
                    <>
                        <div className={classes.itemNameColumn}>
                            <Typography className={classes.itemName}>{props.item.itemName}</Typography>
                            <Typography className={classes.itemType}>{itemTypeLabel}</Typography>
                        </div>
                        <div className={classes.quantityColumn}>
                            <CounterField
                                minLimit={1}
                                value={props.item.quantity}
                                disabled={props.item.unlimited}
                                className={classes.quantityField}
                                onChange={value => props.onChange?.({ ...props.item, quantity: value })}
                            />
                            <Checkbox
                                inlineLabel="Unlimited"
                                checked={props.item.unlimited}
                                className={classes.unlimitedCheckbox}
                                onChange={value => props.onChange?.({ ...props.item, unlimited: value, quantity: 1 })}
                            />
                        </div>
                        <div className={classes.priceColumn}>
                            <Typography className={classes.priceLabel}>Price</Typography>
                            <PriceField2
                                scale={2}
                                value={props.item.amount}
                                className={classes.priceField}
                                onChange={changeItemPriceHandler}
                            />
                        </div>
                        <div className={classes.totalColumn}>
                            <Typography className={classes.totalLabel}>Total</Typography>
                            <Typography className={classes.totalAmount}>${totalItemPrice}</Typography>
                        </div>
                    </>
                )}
            </div>

            {/* TODO: This is a temporary solution to display the variable pricing. */}
            {service && isVariablePrice && (
                <CollapsibleAlert
                    title="This service's pricing varies by pet size:"
                    className={classes.alert}
                >
                    <div className={classes.serviceVariablePrices}>
                        {(() => {
                            // Group prices by pet type and size
                            const groupedPrices = service.pricing.variablePrice?.prices.reduce((acc, variable) => {
                                const key = `${variable.petType?.plural}-${variable.petSize?.name}`;
                                if (!acc[key]) {
                                    acc[key] = [];
                                }
                                acc[key].push(variable);
                                return acc;
                            }, {} as Record<string, typeof service.pricing.variablePrice.prices>);
                            return Object.entries(groupedPrices || {}).map(([key, variables]) => (
                                <div key={key}>
                                    {variables.map(variable => (
                                        <div
                                            key={variable.uuid}
                                            className={classes.serviceVariablePrice}
                                        >
                                            <Typography>{variable.petType?.plural}</Typography>
                                            <Typography>{variable.petSize?.name}</Typography>
                                            <Typography>{variable.hairLength?.name}</Typography>
                                            <Typography>
                                                <b>${variable.price?.toFixed(2)}</b>
                                            </Typography>
                                        </div>
                                    ))}
                                </div>
                            ));
                        })()}
                    </div>
                </CollapsibleAlert>
            )}
        </div>
    );
};
